import type { RecordingPayload } from '@Application/Beneficiaries/Interviews/Actions/interviewsFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `completedAt` is an instant (ISO 8601 with time), unlike `InterviewView.conductedOn`: straight
 * `new Date(iso)` conversion is correct here. `interviewId` is not on `RecordingPayload`: it is
 * carried by `RecordingCreatedAction` and passed in here, so a withdrawal handler can purge this
 * View by a direct comparison, without ever correlating through `InterviewView.recordingId` — the
 * very field the same handler is about to null out. `source`, like `status`, is an enum: wrapped in
 * `LabeledValueView`, never read as a bare string.
 *
 * The constructor takes the raw fields rather than a payload, exactly like `InterviewView`: `facility`
 * is present on `RecordingPayload` but never copied here, so a setter going through the payload
 * constructor would have to fabricate one to satisfy the type. `fromPayload` is the one place the wire
 * shape is read.
 */
class RecordingView {
	readonly id: number;
	readonly interviewId: number;
	readonly status: LabeledValueView;
	readonly source: LabeledValueView;
	readonly mimeType: string | null;
	readonly durationMs: number | null;
	readonly segmentCount: number;
	readonly byteSize: number;
	readonly completedAt: Date | null;

	constructor(
		id: number,
		interviewId: number,
		status: LabeledValueView,
		source: LabeledValueView,
		mimeType: string | null,
		durationMs: number | null,
		segmentCount: number,
		byteSize: number,
		completedAt: Date | null,
	) {
		this.id = id;
		this.interviewId = interviewId;
		this.status = status;
		this.source = source;
		this.mimeType = mimeType;
		this.durationMs = durationMs;
		this.segmentCount = segmentCount;
		this.byteSize = byteSize;
		this.completedAt = completedAt;

		Object.freeze(this);
	}

	static fromPayload(payload: RecordingPayload, interviewId: number): RecordingView {
		return new RecordingView(
			payload.id,
			interviewId,
			new LabeledValueView(payload.status),
			new LabeledValueView(payload.source),
			payload.mimeType,
			payload.durationMs,
			payload.segmentCount,
			payload.byteSize,
			payload.completedAt !== null ? new Date(payload.completedAt) : null,
		);
	}

	/**
	 * `segmentCount` is the number of segments the server confirms holding, never the local transfer
	 * progress (`OutboxItem.progress`, in bytes) — the two never derive from one another.
	 */
	withSegmentCount(segmentCount: number): RecordingView {
		return new RecordingView(
			this.id,
			this.interviewId,
			this.status,
			this.source,
			this.mimeType,
			this.durationMs,
			segmentCount,
			this.byteSize,
			this.completedAt,
		);
	}
}

export default RecordingView;
